import { Box, CircularProgress, Snackbar } from '@mui/material';
import Alert from '@mui/material/Alert';
import { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  DownloadMediasEventsEnum,
  useDownloadMediasNotifier,
} from '../../hooks/useDownloadMediasNotifier';
import LangEnum from '../../lang/LangEnum';
import { downloadMediasFrame } from '../../modules/orders/dowload-medias/downloadMediasFrame';
import { useTranslation } from '../../modules/translation/TranslationContext';
import { useDownloadMediasNotifierStore } from '../../stores/DownloadMediasNotifier.store';
import snackbarStore from '../../stores/Snackbar.store';

const useStyles = makeStyles()((theme) => ({
  snackbar: {
    marginTop: theme.spacing(8),
    zIndex: 1299,
  },
  alert: {
    marginTop: 4,
    backgroundColor: theme.palette.text.primary,
    '& .MuiAlert-icon': {
      alignItems: 'center',
    },
  },
  progressPrimary: {
    color: theme.palette.common.white,
  },
}));

type MediasProcessedSocket = { orderId: string; url: string; error: { message: string } };

const DownloadMediasNotifier = () => {
  const { translate } = useTranslation();
  const [snackbarOpenness, setSnackbarOpenness] = useState<boolean>(true);

  const { classes } = useStyles();

  const socket = useDownloadMediasNotifier();

  const { ordersDownloadingMedias, removeOrder } = useDownloadMediasNotifierStore();

  useEffect(() => {
    if (!socket) {
      return;
    }
    let timeout: NodeJS.Timeout;
    socket.on(
      DownloadMediasEventsEnum.MEDIAS_PROCESSED,
      ({ orderId, url, error }: MediasProcessedSocket) => {
        const removedOrder = removeOrder(orderId);

        if (error) {
          setSnackbarOpenness(false);

          if (url) {
            snackbarStore.handleMessage({
              type: 'info',
              message: translate(LangEnum.SNACKBAR_PARTIAL_ERROR_DOWNLOADING_MEDIA, {
                errorMessage: error.message,
              }),
            });
          } else {
            snackbarStore.handleMessage({
              type: 'error',
              message: translate(LangEnum.SNACKBAR_ERROR_DOWNLOADING_MEDIA, {
                errorMessage: error.message,
              }),
            });
          }
        }

        if (removedOrder) {
          downloadMediasFrame(url);
        }

        timeout = setTimeout(() => setSnackbarOpenness(false), 2000);
      },
    );

    return () => {
      socket.off(DownloadMediasEventsEnum.MEDIAS_PROCESSED);

      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [removeOrder, socket, translate]);

  return (
    <Snackbar
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      className={classes.snackbar}
      open={snackbarOpenness}
      slotProps={{
        content: {
          'aria-describedby': 'message-id',
        },
      }}
    >
      <Box
        style={{ maxWidth: 300 }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {ordersDownloadingMedias.map((order) => (
          <Alert
            key={order.id}
            elevation={6}
            className={classes.alert}
            variant="filled"
            icon={
              <CircularProgress size={16} classes={{ colorPrimary: classes.progressPrimary }} />
            }
          >
            {translate(LangEnum.ORDER_DOWNLOAD_MEDIAS_MESSAGE, { order: order.code })}
          </Alert>
        ))}
      </Box>
    </Snackbar>
  );
};

export default DownloadMediasNotifier;
